/**
 * CrmDealCreationOriginFilter
 * ----------------------------
 * Filtro avançado de "Origem do Cadastro" para a listagem de Negócios.
 *
 * - Multi-seleção entre 6 tipos fixos: Manual, Importação, Webhook,
 *   Agendamento, Fluxo, API.
 * - Quando "Importação" está selecionada, exibe um sub-seletor para escolher
 *   a lista (job de importação) que originou os negócios.
 *
 * Vive dentro do Sheet "Filtros Avançados" (botão "Mais"), por isso o trigger
 * usa largura 100% e segue o padrão visual dos demais filtros do Sheet.
 *
 * RELEASE: 2026-05-16 v1.0.0 - Criação inicial.
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 */
import React, { useState } from 'react';
import {
  Sparkles,
  Check,
  ChevronDown,
  X,
  User,
  Upload,
  Webhook,
  CalendarClock,
  Workflow,
  Plug,
  Handshake,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDealImportJobsLookup } from '@/hooks/useDealImportJobsLookup';

/** Tipos de origem suportados (espelham `crm_deals.creation_origin->>'type'`). */
export type CreationOriginType =
  | 'manual'
  | 'import'
  | 'webhook'
  | 'scheduling'
  | 'flow'
  | 'api'
  | 'affiliate';

/** Valor controlado pelo componente. */
export interface CreationOriginFilterValue {
  types: CreationOriginType[];
  importJobId: string | null;
}

/** Catálogo fixo dos 7 tipos (sempre exibidos, mesmo sem dados no tenant). */
const ORIGIN_TYPES: Array<{
  value: CreationOriginType;
  label: string;
  icon: typeof User;
  dotClass: string;
}> = [
  { value: 'manual',     label: 'Manual',      icon: User,          dotClass: 'bg-slate-400' },
  { value: 'import',     label: 'Importação',  icon: Upload,        dotClass: 'bg-blue-500' },
  { value: 'webhook',    label: 'Webhook',     icon: Webhook,       dotClass: 'bg-purple-500' },
  { value: 'scheduling', label: 'Agendamento', icon: CalendarClock, dotClass: 'bg-emerald-500' },
  { value: 'flow',       label: 'Fluxo',       icon: Workflow,      dotClass: 'bg-amber-500' },
  { value: 'api',        label: 'API',         icon: Plug,          dotClass: 'bg-zinc-500' },
  // v1.3.0 (2026-05-16): Afiliado — negócios criados pelo módulo Indicação
  { value: 'affiliate',  label: 'Afiliado',    icon: Handshake,     dotClass: 'bg-pink-500' },
];

interface CrmDealCreationOriginFilterProps {
  value: CreationOriginFilterValue;
  onChange: (next: CreationOriginFilterValue) => void;
  className?: string;
}

/**
 * Filtro de origem de cadastro do negócio (multi-tipo + sub-filtro de
 * importação). Renderiza um botão pill que abre um Popover com checkboxes.
 */
export function CrmDealCreationOriginFilter({
  value,
  onChange,
  className,
}: CrmDealCreationOriginFilterProps) {
  const [open, setOpen] = useState(false);
  const importSelected = value.types.includes('import');

  // Só carrega o catálogo de importações quando o usuário escolheu "Importação"
  // — evita request desnecessário e mantém a tela leve.
  const { data: importJobs = [], isLoading: isLoadingJobs } = useDealImportJobsLookup({
    enabled: importSelected,
  });

  // Toggle de um tipo. Ao desmarcar "import", limpa também o importJobId.
  const toggleType = (type: CreationOriginType) => {
    if (value.types.includes(type)) {
      onChange({
        types: value.types.filter((t) => t !== type),
        importJobId: type === 'import' ? null : value.importJobId,
      });
    } else {
      onChange({ ...value, types: [...value.types, type] });
    }
  };

  const clearAll = () => onChange({ types: [], importJobId: null });

  // Texto do trigger
  const triggerLabel =
    value.types.length === 0
      ? 'Origem Cadastro'
      : value.types.length === 1
        ? ORIGIN_TYPES.find((t) => t.value === value.types[0])?.label || 'Origem Cadastro'
        : `${value.types.length} origens`;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'w-full justify-between gap-1.5 font-normal h-8 text-xs border-0 bg-background rounded-full',
              className,
            )}
          >
            <span className="flex items-center gap-1.5 min-w-0 truncate">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{triggerLabel}</span>
            </span>
            <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-2" align="start">
          <div className="space-y-1">
            {ORIGIN_TYPES.map((t) => {
              const isSelected = value.types.includes(t.value);
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => toggleType(t.value)}
                  className={cn(
                    'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs text-left',
                    'hover:bg-accent hover:text-accent-foreground transition-colors',
                  )}
                >
                  <div
                    className={cn(
                      'h-4 w-4 rounded border flex items-center justify-center shrink-0',
                      isSelected ? 'border-primary bg-primary' : 'border-muted',
                    )}
                  >
                    {isSelected && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <span className={cn('w-2 h-2 rounded-full shrink-0', t.dotClass)} />
                  <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{t.label}</span>
                </button>
              );
            })}
          </div>
          {value.types.length > 0 && (
            <div className="border-t mt-2 pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-xs text-muted-foreground"
                onClick={clearAll}
              >
                <X className="h-3 w-3 mr-1" />
                Limpar seleção
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Sub-seletor da lista de importação — só aparece com "Importação" marcada */}
      {importSelected && (
        <div className="pl-3 border-l-2 border-blue-200 dark:border-blue-900 space-y-1">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Lista de Importação
          </p>
          <Select
            value={value.importJobId ?? '__all__'}
            onValueChange={(v) =>
              onChange({ ...value, importJobId: v === '__all__' ? null : v })
            }
          >
            <SelectTrigger className="h-8 text-xs w-full">
              <SelectValue
                placeholder={isLoadingJobs ? 'Carregando…' : 'Todas as listas'}
              />
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="__all__" className="text-xs">
                Todas as listas
              </SelectItem>
              {importJobs.map((j) => {
                const label =
                  j.name?.trim() ||
                  j.source_file_name?.trim() ||
                  `Importação ${j.id.slice(0, 8).toUpperCase()}`;
                return (
                  <SelectItem key={j.id} value={j.id} className="text-xs">
                    <span className="flex flex-col">
                      <span className="truncate">{label}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {format(new Date(j.created_at), "dd/MM/yyyy 'às' HH:mm", {
                          locale: ptBR,
                        })}
                        {typeof j.succeeded_items === 'number' &&
                          ` · ${j.succeeded_items} ok`}
                      </span>
                    </span>
                  </SelectItem>
                );
              })}
              {!isLoadingJobs && importJobs.length === 0 && (
                <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                  Nenhuma importação encontrada
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
